import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Box, 
  Typography, 
  Paper,
  useTheme
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface DropzoneProps {
  onDrop: (files: File[]) => void;
  disabled?: boolean;
}

const Dropzone: React.FC<DropzoneProps> = ({ onDrop, disabled }) => {
  const theme = useTheme();
  
  const onDropCallback = useCallback((acceptedFiles: File[]) => {
    onDrop(acceptedFiles);
  }, [onDrop]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropCallback,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif']
    },
    disabled,
    multiple: true
  });

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ width: '100%' }}
    >
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {isDragActive ? (
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          ) : (
            <ImageIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          )}
          <Typography variant="h6" align="center" color="text.primary">
            {isDragActive
              ? 'Drop your webtoon panels here'
              : 'Drag and drop your webtoon panels here'}
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary">
            or click to select files
          </Typography>
          <Typography variant="caption" align="center" color="text.secondary">
            Supports PNG, JPG, JPEG, GIF • Select multiple files
          </Typography>
        </Box>
      </Paper>
    </motion.div>
  );
};

export default Dropzone; 